import { useEffect, useState } from 'react';
import { MdInfoOutline, MdSend } from 'react-icons/md';

import { Avatar, SegmentedControl } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Autocomplete, ContactInput, Input } from '@/components/Inputs';
import { guestContactOptions, pickedGuestContacts } from '@/store/contacts/contacts.mappers';
import { useContacts } from '@/store/contacts/contacts.store';
import {
  guestCountError,
  inviteExpiryError,
  inviteExpiryNote,
  parseGuestCount,
} from '@/store/invites/invites.mappers';
import type { GuestInvitee } from '@/store/invites/invites.store';

import s from '../EventWizardModal.module.scss';

/** Someone already in the viewer's contacts, or anyone else, reached by a link. */
type InviteMode = 'contacts' | 'link';
const INVITE_MODES = [
  { value: 'contacts', label: 'Contacts' },
  { value: 'link', label: 'Invite by link' },
] as const;

interface InvitePanelProps {
  name: string;
  onNameChange: (value: string) => void;
  contact: string;
  onContactChange: (value: string) => void;
  /** Empties the typed name and contact; the panel itself never closes. */
  onClear: () => void;
  /** Whom to invite — the picked contacts, or the guest typed in — by the open tab. */
  onSend: (recipients: GuestInvitee[]) => void;
  /** Person id → why that contact can't be picked here (already chosen elsewhere). */
  unavailable: Record<string, string>;
  /** The expiry picked for every invitation of the event; none keeps the default rule. */
  expiresAt: string | null;
  onExpiresAtChange: (value: string) => void;
  /** The event's end: the latest expiry that may be picked. */
  eventEndsAt: Date | null;
}

export function InvitePanel({
  name,
  onNameChange,
  contact,
  onContactChange,
  onClear,
  onSend,
  unavailable,
  expiresAt,
  onExpiresAtChange,
  eventEndsAt,
}: InvitePanelProps) {
  const contacts = useContacts((state) => state.contacts);
  const getContacts = useContacts((state) => state.getContacts);
  const [query, setQuery] = useState('');
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  const [mode, setMode] = useState<InviteMode>('contacts');
  /** Contact id → the guest count typed for that picked contact. */
  const [pickedCounts, setPickedCounts] = useState<Record<string, string>>({});
  const [typedCount, setTypedCount] = useState('0');
  const pickedCount = (id: string) => pickedCounts[id] ?? '0';

  useEffect(() => {
    void getContacts();
  }, [getContacts]);

  const contactOptions = guestContactOptions(contacts);
  const search = query.trim().toLowerCase();
  const options = contactOptions
    .filter((option) => !pickedIds.includes(option.value))
    .filter((option) => option.name.toLowerCase().includes(search))
    .map((option) => {
      const taken = unavailable[option.personId];
      return {
        value: option.value,
        disabled: option.disabled || !!taken,
        label: (
          <>
            <Avatar src={option.avatar} firstName={option.name} size={28} />
            {taken ? `${option.name} — ${taken}` : option.label}
          </>
        ),
      };
    });
  const selectedOptions = contactOptions
    .filter((option) => pickedIds.includes(option.value))
    .map((option) => ({ value: option.value, label: option.name }));

  const byContacts = mode === 'contacts';
  const countsValid = byContacts
    ? pickedIds.every((id) => parseGuestCount(pickedCount(id)) !== undefined)
    : parseGuestCount(typedCount) !== undefined;
  const canSend =
    countsValid && (byContacts ? pickedIds.length > 0 : !!name.trim() && !!contact.trim());
  const isEmpty = byContacts ? pickedIds.length === 0 && !query : !name && !contact;

  // The panel stays open after sending, so the open tab starts over empty for the next guest.
  const clear = () => {
    if (byContacts) {
      setPickedIds([]);
      setQuery('');
      setPickedCounts({});
    } else {
      onClear();
      setTypedCount('0');
    }
  };

  const expiryError = inviteExpiryError(expiresAt, eventEndsAt);

  return (
    <div className={s.invitePanel}>
      <SegmentedControl
        size="sm"
        as="radio"
        options={INVITE_MODES}
        value={mode}
        onChange={setMode}
        aria-label="Whom to invite"
        className={s.inviteModes}
      />
      {byContacts ? (
        <>
          <Autocomplete
            label="Contacts"
            placeholder={pickedIds.length > 0 ? undefined : 'Choose contacts'}
            value={query}
            onChange={setQuery}
            options={options}
            selectedOptions={selectedOptions}
            onMultipleItemsSelect={(selected) => {
              setPickedIds(selected.map(({ value }) => value));
              setQuery('');
            }}
          />
          {selectedOptions.map(({ value, label }) => (
            <Input
              key={value}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              label={`Additional Guests for ${label}`}
              value={pickedCount(value)}
              onChange={(e) => setPickedCounts((prev) => ({ ...prev, [value]: e.target.value }))}
              error={guestCountError(pickedCount(value))}
            />
          ))}
        </>
      ) : (
        <>
          <p className={s.inviteSub}>Your guest will use a link in email or SMS to join.</p>
          <Input
            label="Guest Name"
            mandatory
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Alex Morgan"
          />
          <ContactInput
            label="Mobile or Email"
            value={contact}
            onChange={onContactChange}
            onDeviceContactPick={onNameChange}
            mandatory
          />
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            label="Additional Guests"
            value={typedCount}
            onChange={(e) => setTypedCount(e.target.value)}
            error={guestCountError(typedCount)}
          />
        </>
      )}
      <DateTimePicker
        label="Link Expires"
        placeholder="When the event starts"
        value={expiresAt}
        onChange={onExpiresAtChange}
        minDate={new Date()}
        maxDate={eventEndsAt ?? undefined}
        showTimeInput
        error={expiryError}
      />
      <div className={s.expiryNote}>
        <MdInfoOutline />
        <span>{inviteExpiryNote(expiresAt)}</span>
      </div>
      <div className={s.inviteActions}>
        <ButtonOutlined onClick={clear} disabled={isEmpty}>
          Clear
        </ButtonOutlined>
        <Button
          onClick={() => {
            onSend(
              byContacts
                ? pickedIds.flatMap((id) =>
                    pickedGuestContacts(contacts, [id]).map((invitee) => ({
                      ...invitee,
                      additionalGuests: parseGuestCount(pickedCount(id)),
                    })),
                  )
                : [
                    {
                      name: name.trim(),
                      contact: contact.trim(),
                      additionalGuests: parseGuestCount(typedCount),
                    },
                  ],
            );
            clear();
          }}
          disabled={!canSend || !!expiryError}
        >
          Send Invitation <MdSend />
        </Button>
      </div>
    </div>
  );
}
